import { useState } from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { UpdateVolunteerForm, VolunteerFormState } from '../types';
import { FormPanel, FormSectionTitle, ValidationText } from '../styles';
import { Text } from '@/components/UI/Typography/Text';
import { Button } from '@/components/UI/Button';
import { Flex } from '@/layout';
import { theme } from '@/theme/Theme';
import { SignatureModal } from '@/components/UI/Modal';
import { formatPblDisplayDate } from '@/utils/pblDate';
import { SectionTitle } from '../../CaseProgressDetail/styles';

interface Props {
  form: VolunteerFormState;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isTablet: boolean;
  updateForm: UpdateVolunteerForm;
}

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const DeclarationStep = ({ form, errors, isTablet, updateForm }: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);

  const saveSignature = (dataUrl: string) => {
    updateForm('declaration_signature', dataUrl);
    updateForm('declaration_signed_at', formatDate(new Date()));
  };

  const resetSignature = () => {
    updateForm('declaration_signature', '');
    updateForm('declaration_signed_at', '');
  };

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>{t('pbl.lawyerRegistration.declaration.section', { defaultValue: 'Section 10' })}</SectionTitle>
      <FormSectionTitle>
        {t('pbl.lawyerRegistration.declaration.title', { defaultValue: 'Declaration' })}
      </FormSectionTitle>
      <Text>
        {t('pbl.lawyerRegistration.declaration.paragraph21', {
          defaultValue:
            '21. I declare that all information provided in this form and all uploaded documents are true, accurate and complete and I will inform the Scheme immediately upon change of any particulars given in this form. I hereby apply for registration as a panel lawyer of the FreelegalLink Pro Bono Lawyers Panel.',
        })}
      </Text>

      <div style={{ marginTop: 28 }}>
        <FormSectionTitle>
          {t('pbl.lawyerRegistration.declaration.signatureTitle', { defaultValue: 'Signature' })}
        </FormSectionTitle>
        <Flex alignItems="center" gridGap={'20px'}>
          {t('pbl.lawyerRegistration.declaration.applicantSignature', { defaultValue: "Applicant's Signature :" })}
          <Button
            data-volunteer-field="declaration_signature"
            variant={form.declaration_signature ? 'regular' : 'primary'}
            size="md"
            type="button"
            onClick={() => {
              if (!form.declaration_signature) {
                setIsSignModalOpen(true);
              }
            }}
          >
            {form.declaration_signature
              ? t('pbl.common.actions.signed', { defaultValue: 'Signed' })
              : t('pbl.common.actions.sign', { defaultValue: 'Sign' })}
          </Button>
          <Text color={theme.colors.gray400}>
            {t('pbl.lawyerRegistration.declaration.date', { defaultValue: 'Date :' })}{' '}
            {form.declaration_signed_at ? formatPblDisplayDate(form.declaration_signed_at) : ''}
          </Text>
        </Flex>
        {form.declaration_signature ? (
          <div style={{ marginTop: 10, marginLeft: 4 }}>
            <Text
              as="button"
              type="button"
              onClick={resetSignature}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                color: theme.colors.primary300,
                textDecoration: 'underline',
                cursor: 'pointer',
                marginBottom: 8,
              }}
            >
              {t('pbl.common.actions.resign', { defaultValue: 'Re-sign' })}
            </Text>
            <img
              src={form.declaration_signature}
              alt={t('pbl.lawyerRegistration.declaration.signatureImageAlt', { defaultValue: 'Applicant signature' })}
              style={{
                display: 'block',
                maxWidth: 220,
                maxHeight: 88,
                objectFit: 'contain',
                border: '1px solid #d1d5db',
              }}
            />
          </div>
        ) : null}
        {errors.declaration_signature?.message && (
          <ValidationText>{errors.declaration_signature.message}</ValidationText>
        )}
        {errors.declaration_signed_at?.message && (
          <ValidationText>{errors.declaration_signed_at.message}</ValidationText>
        )}
      </div>

      <SignatureModal isOpen={isSignModalOpen} onClose={() => setIsSignModalOpen(false)} onConfirm={saveSignature} />
    </FormPanel>
  );
};

export default DeclarationStep;
